import { laptopMediumMax, mobileMax, mobileXXLargeMax } from '@propertypal/shared/src/constants/mediaQueries';
import platform from '@propertypal/shared/src/utils/platform';
import { Container as ButtonContainer } from '@propertypal/web-ui/src/buttons/Button.style';
import { Container as DropButton } from '@propertypal/web-ui/src/buttons/DropButton.style';
import { Label } from '@propertypal/web-ui/src/inputs/Checkbox.style';
import MultiSelect from '@propertypal/web-ui/src/inputs/MultiSelect';
import {
  InputBox as MultiSelectBox,
  Placeholder as MultiSelectPlaceholder,
} from '@propertypal/web-ui/src/inputs/MultiSelect.style';
import SelectInput from '@propertypal/web-ui/src/inputs/SelectInput';
import { OptionItem } from '@propertypal/web-ui/src/inputs/SelectInput.style';
import Toggle from '@propertypal/web-ui/src/inputs/Toggle';
import { Container as ToggleSwitchContainer } from '@propertypal/web-ui/src/inputs/ToggleSwitch.style';
import { Column } from '@propertypal/web-ui/src/layout';
import { MAX_WIDTH } from '@propertypal/web-ui/src/layout/PageWrapper.style';
import { InputLabel } from '@propertypal/web-ui/src/typography';
import Link from 'next/link';
import styled, { css } from 'styled-components';

export const filterBreakpoint = 'max-width: 940px';

export const SearchBarForm = styled.div`
  width: 100%;
  padding: 10px 30px;
  flex-direction: row;
  display: flex;
  justify-content: center;
  max-width: ${MAX_WIDTH - 0}px;
  margin: 0 auto;

  ${ButtonContainer} {
    width: 172px;
  }

  @media (${laptopMediumMax}) {
    padding: 10px;

    ${ButtonContainer} {
      width: 60px;

      svg {
        margin-right: 0 !important;
      }

      .buttonText {
        display: none;
      }
    }
  }
`;

export const SearchButton = styled.button`
  width: 49px;
  height: 49px;
  border-radius: 24px;
  background-color: ${({ theme }) => theme.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.white};
`;

export const DropBox = styled.div`
  position: relative;
  display: flex;
  height: 30px;
  align-items: center;

  @media (${filterBreakpoint}) {
    display: none;
  }
`;

export const TotalIcon = styled.span`
  display: inline-block;
  background-color: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.white};
  border-radius: 4px;
  padding: 2px 5px;
  font-weight: 600;
  margin-left: -10px;
`;

export const DropItem = styled.button<{ $active: boolean }>`
  border-left: 1px solid ${({ theme }) => theme.keyline};
  padding: 0 15px;
  font-size: 15px;
  color: ${({ theme }) => theme.textDark};

  // This forces the element to size the element as if the text is bold
  // That way whenever whenever font-weight: bold is applied the element size doesn't increase
  &::before {
    display: block;
    content: attr(title);
    font-weight: bold;
    height: 0;
    color: transparent;
    overflow: hidden;
    visibility: hidden;
  }

  ${({ $active }) =>
    $active &&
    css`
      font-weight: bold;
    `}
`;

export const SearchInputIcon = styled.button`
  padding: 10px 8px;
  height: 100%;

  svg {
    color: ${(props) => props.theme.textDark};
  }

  &:hover svg {
    color: ${(props) => props.theme.primary};
  }

  @media (${mobileXXLargeMax}) {
    display: none;
  }
`;

export const InputContainer = styled.div`
  position: relative;
  background: ${(props) => props.theme.background};
  flex-direction: row;
  display: flex;
  height: 60px;
  align-items: center;
  flex: 1;
  border-radius: 32px;
  box-shadow: 0 3px 8px 0 rgba(0, 0, 0, 0.1);
  border: solid 1px ${({ theme }) => theme.keyline};
  padding: 0 5px 0 10px;
`;

export const FiltersButton = styled.button<{ active: boolean }>`
  position: relative;
  display: none;
  height: 100%;
  align-items: center;
  padding: 0 20px 0 15px;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.active ? props.theme.primary : props.theme.textDark)};

  @media (${filterBreakpoint}) {
    display: flex;
  }

  @media (${mobileMax}) {
    padding: 0 15px;
  }

  &:before {
    content: ' ';
    background-color: ${(props) => props.theme.backgroundLight};
    position: absolute;
    width: 1px;
    height: 34px;
    left: 0;
  }
`;

export const FilterContainer = styled.div`
  position: relative;
  background-color: ${(props) => props.theme.backgroundLighter};
`;

export const FiltersPane = styled.div<{ $overflow?: boolean }>`
  position: absolute;
  top: 94px;
  left: 0;
  background-color: ${(props) => props.theme.backgroundLighter};
  width: 100%;
  transition: 0.4s all ease;
  padding: 20px;
  z-index: 5;

  ${(props) =>
    props.$overflow &&
    `
    max-height: calc(100vh - 230px);
    overflow: auto;
  `}

  @media (${filterBreakpoint}) {
    display: none;
  }
`;

export const FilterPaneContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 auto;
  width: 100%;
  max-width: ${MAX_WIDTH}px;
  padding: 0 30px;
`;

export const SearchModal = styled.div<{ active: boolean }>`
  position: absolute;
  background-color: ${(props) => props.theme.background};
  width: 100%;
  opacity: 0;
  top: 0;
  z-index: 30;
  transition: 300ms all;
  visibility: hidden;
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;

  ${(props) =>
    props.active &&
    css`
      opacity: 1;
      visibility: visible;
    `}
`;

export const StyledSelect = styled(SelectInput)`
  max-width: 125px;

  ${DropButton} {
    box-shadow: none !important;

    span {
      font-size: 15px !important;
    }
  }
`;

export const SearchModalHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  border-bottom: 1px solid ${({ theme }) => theme.keyline};
  padding: 15px;
`;

export const ScrollDiv = styled.div`
  position: relative;
  width: 100%;
  -webkit-overflow-scrolling: touch;
  overflow-y: scroll;
  padding: 15px;
  flex: 1;
  gap: 30px;
  display: flex;
  flex-direction: column;

  label {
    font-family: ${platform.headingFont};
    font-size: 18px;
    padding-left: 0;
  }

  ${Label} {
    font-family: inherit;
    font-size: 16px;
  }
`;

export const CloseIcon = styled.button`
  display: flex;
  padding: 10px;
`;

export const ModalFooter = styled.div`
  display: flex;
  width: 100%;
  background-color: ${(props) => props.theme.background};
  border-top: 1px solid ${(props) => props.theme.keyline};
  flex-direction: row;
  padding: 10px;
  justify-content: center;
  z-index: 1;

  @media (max-height: 500px) {
    bottom: 0;
  }
`;

export const InputRow = styled.div`
  display: flex;
  flex-direction: row;

  @media (${filterBreakpoint}) {
    flex-direction: column;
  }
`;

export const InputColumn = styled.div<{ direction?: string }>`
  display: flex;
  flex: 1;
  flex-direction: row;
`;

// Currency toggle on desktop needs space to the right due to Price Range
// But on mobile, needs space to the left due to Select a Category
export const CurrencyToggle = styled(Toggle)`
  max-width: 160px;
  margin-right: 15px;

  @media (${filterBreakpoint}) {
    margin-left: 20px;
    margin-right: 0;
  }
`;

export const SmartMultiSelect = styled(MultiSelect)`
  width: 100%;
  max-width: 260px;

  @media (${filterBreakpoint}) {
    max-width: none;
  }
`;

export const SmartColumn = styled(Column)<{ gap?: string }>`
  flex: 1;

  ${Label} {
    font-size: 14px;
  }

  &:not(:last-child) {
    margin-right: 20px;
  }

  ${MultiSelectPlaceholder} {
    font-size: 14px;
    font-weight: 600;
  }

  ${OptionItem} {
    font-weight: 600;
    font-size: 14px;
  }

  @media (${filterBreakpoint}) {
    align-items: center;
    flex-direction: row;
    flex: 0 0 calc(50% - 5px);

    &:not(:last-child) {
      margin-right: 0;
      margin-bottom: 20px;
    }

    ${MultiSelectBox} {
      box-shadow: none;
      border-radius: 4px;
    }

    ${ToggleSwitchContainer} {
      margin-left: 20px;
    }

    ${InputLabel} {
      min-width: 175px;
      margin-bottom: 0;
    }
  }
`;

export const SmartFiltersWrapper = styled.div`
  display: flex;
  margin-top: 30px;
  width: 100%;

  @media (max-width: 1140px) {
    flex-direction: column;
  }
`;

export const EPCWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${Label} {
    font-size: 14px;
  }
`;

export const Dropdown = styled.div`
  position: absolute;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  z-index: 5;
  top: 48px;
  padding: 20px;
  width: calc(100vw - 30px);
  max-width: 540px;
  right: -235px;
  display: flex;
  flex-wrap: wrap;

  label {
    font-size: 20px;
    font-family: ${platform.headingFont};
    padding-left: 0;
  }
`;

export const PartnershipBar = styled(Link)`
  width: 100%;
  text-align: center;
  color: ${({ theme }) => theme.textDark};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding-top: 15px;
  font-size: 14px;

  img {
    height: 30px;
  }
`;

export const SmallPartnership = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 5px 0 -50px 20px;

  span {
    font-size: 12px;
  }

  img {
    width: 100px;
  }
`;

export const FiltersDropdown = styled.div`
  position: absolute;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  z-index: 5;
  top: 48px;
  width: calc(100vw - 80px);
  max-width: 980px;
  right: -170px;
  max-height: calc(100vh - 160px);
  display: flex;
  flex-direction: column;
`;

export const FiltersScroll = styled.div`
  flex: 1;
  overflow-y: scroll;
  padding: 20px;
`;

export const FiltersBottom = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.background};
  border-top: 1px solid ${({ theme }) => theme.keyline};
  display: flex;
  padding: 10px;
  justify-content: center;
  gap: 15px;
`;
